import { useMemo, useCallback, useState, useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import { useAuth } from './useAuth';
import { supabase } from '../services/supabase';
import { CAPABILITIES_MAP, SYSTEM_ROLES } from '../constants/capabilities';
import { resolvePortalType, PORTAL_TYPES } from '../services/capabilityRegistryService';
import { resolveSystemRoleId, rolesSystemeEndosses } from '../utils/roleAccess';
import { computeCockpitTemplateKey } from '../services/cockpitTemplates';
import { isAssignmentActive } from '../services/scopeHierarchy';
import { effectiveScopeSets, applyScopeSets } from '../services/scopeFilterEngine';

// Repli stable pour `capOverrides` (voir plus bas) : `{}` littéral créerait un
// nouvel objet à CHAQUE rendu, faisant échouer toute comparaison de référence.
const EMPTY_CAP_OVERRIDES = {};
const EMPTY_LIST = Object.freeze([]);

// Cache process-local des affectations dynamiques, clé `userId:tenantId`.
// Trouvé le 2026-10-09 (vue Forums : clic sur « Créer un forum » sans AUCUN
// effet) : CapabilityGate démonte/remonte cette instance du hook à CHAQUE
// navigation vers une route gardée (commentaire plus bas), et sans cache
// CHAQUE navigation relançait la requête `user_role_assignments` et repassait
// `assignmentsLoading` à `true` — le temps du aller-retour réseau,
// CapabilityGate affiche `<PageLoader/>` et PortalGate `null` à la place de
// la vue réelle, qui est donc démontée puis remontée à chaque clic dans le
// menu. Toute vue ayant un état local interactif (ex. une modale `useState`)
// perd cet état au remontage suivant — symptôme observé : la modale de
// création de forum ne s'ouvrait plus jamais après le premier aller-retour.
// Conforme à l'intention déjà documentée plus bas : « une seule fois par
// session ». Clé incluant le tenant : un Master qui inspecte plusieurs
// tenants ne doit pas réutiliser les affectations de l'un pour l'autre.
const dynamicAssignmentsCache = new Map(); // "userId:tenantId" -> assignments[]

// Tests uniquement : chaque cas réutilise le même id utilisateur/tenant
// factice, sans ce reset le premier test qui résout la requête pollue tous
// les suivants dans le même fichier (le cache est volontairement un module
// singleton, partagé entre tous les composants réels d'une même session).
export const __resetDynamicAssignmentsCacheForTests = () => dynamicAssignmentsCache.clear();

/**
 * Rectorium RBAC — Capability-Based Access Control
 *
 * Architecture : Capacité métier → Permission → Rôle → Utilisateur
 *
 * Deux modes (tenant.governance_config.rbac_mode) :
 *   'legacy'  — profile.role string → system role → capability grants
 *   'dynamic' — user_role_assignments DB → permissions JSONB (rôles 100%
 *               propres à chaque tenant, capacités assemblables à la carte
 *               via l'écran Gouvernance → Rôles & Permissions — chaque
 *               tenant peut avoir une organisation interne différente).
 *               Les affectations réelles de l'utilisateur sont chargées en
 *               interne par ce hook (aucun appelant n'a besoin de les
 *               fournir) ; passer un tableau en argument reste possible
 *               pour les tests, et court-circuite ce chargement.
 *
 * Usage :
 *   const { can } = useGovAccess();
 *   can('governance', 'read')    → boolean
 *   can('examinations', 'approve') → boolean
 *
 * Principe : les postes/titres (Doyen, Recteur…) sont AFFICHAGE SEUL.
 * Seules les capacités et actions déterminent les droits système.
 */

// ── Bridge legacy → system role ──────────────────────────────────
export const LEGACY_TO_SYSTEM = {
  'Master':                    'master',
  'Administrateur':            'it_admin',
  'IT':                        'it_admin',
  'DG':                        'dg',
  'Direction Générale':        'dg',
  'Recteur':                   'dg',
  'Président':                 'dg',
  'Vice-Chancelier':           'dg',
  'Provost':                   'dg',
  'Principal':                 'dg',
  'Directeur Académique':      'directeur',
  'Direction des écoles':      'directeur',
  'Directeur des écoles':      'directeur',
  'Doyen':                     'directeur',
  "Directeur d'École":         'directeur',
  'Directeur Ecole':           'directeur',
  'Directeur':                 'directeur',
  'Chef de Département':       'directeur',
  'Coordonnateur de Programme':'resp_academique',
  'Coordonnateur de Cycle':    'resp_academique',
  // Ajouté 2026-07-16 (portail rôle coord_niveau, couche Portails Rôles CBAC) —
  // clé manquante jusqu'ici, confirmée absente par grep avant ajout.
  'Coordonnateur de Niveau':   'resp_academique',
  'Responsable Académique':    'resp_academique',
  'Responsable de Filière':    'resp_academique',
  'Référent de Spécialité':    'resp_academique',
  'Chargé Académique':         'resp_academique',
  'Responsable Qualité':       'controle',
  'Manager Qualité':           'controle',
  'Responsable Audit':         'controle',
  'Contrôleur Interne':        'controle',
  'Responsable Conformité':    'controle',
  'Responsable Recherche':     'resp_academique',
  // Arbitrage utilisateur 2026-10-04 (DI-G1, 16.3) : rôle système DÉDIÉ
  // (était un alias de resp_academique, qui ne portait que admissions:read
  // et empêchait toute opération marketing). Voir capability-registry.json,
  // profil dev_inst.
  'Responsable Développement': 'dev_inst',
  'Responsable PMO':           'amelioration',
  'Directeur PMO':             'amelioration',
  'Responsable Innovation':    'amelioration',
  'Responsable Transformation':'amelioration',
  'Gestionnaire Scolarité':    'registraire',
  'Responsable Admissions':    'registraire',
  'Registraire':               'registraire',
  'Registraire Institutionnel':'registraire',
  'Bureau du Registraire':     'registraire',
  'Assistant Registraire':     'registraire',
  'Assistante Registraire':    'registraire',
  'Adjoint Registraire':       'registraire',
  'Adjointe Registraire':      'registraire',
  'Agent Comptable':           'gestionnaire',
  // Rôles CBAC spécialisés v4 (2026-07-14) — miroir de roleAccess.js pour ne
  // pas ajouter à la divergence déjà connue entre les deux copies (à réconcilier
  // séparément — cf. rapport de fin de session).
  'Responsable des Ressources Humaines': 'rrh',
  'Directeur Administratif et Financier': 'daf',
  'Responsable de la Recherche': 'resp_rech',
  'Responsable Logistique et Patrimoine': 'resp_log',
  'Responsable Communication': 'resp_com',
  'Enseignant':                'enseignant',
  'Enseignant Permanent':      'enseignant',
  'Enseignant Associé':        'enseignant',
  'Vacataire':                 'enseignant',
  'Tuteur':                    'enseignant',
  'Encadreur':                 'enseignant',
  'Apprenant':                 'etudiant',
  'Étudiant':                  'etudiant',
  'Parent':                    'parent',
  'Auditeur':                  null,
  'Observateur':               null,
};

// Pre-built system roles lookup
const SYSTEM_ROLES_MAP = Object.fromEntries(SYSTEM_ROLES.map(r => [r.id, r]));

/* ── Capacités réservées à l'opérateur Rectorium (jamais tenant-facing) ──
   Le domaine 'corporate' (CRM, Sales Ops, DevOps, Support Ops…) et les
   capacités d'exploitation plateforme sont le domaine exclusif du Master :
   aucun rôle tenant (y compris DG avec read ALL) ne doit les voir.

   C'est une frontière d'EXPLOITATION, pas une frontière commerciale : elle
   sépare ce qui appartient à Rectorium de ce qui appartient à l'établissement.
   Elle survit donc à la purge du 2026-08-16, qui n'a retiré que ce qui
   conditionnait un accès à un forfait. */
const MASTER_ONLY_DOMAINES = ['corporate'];

/**
 * LES CAPACITÉS QU'UN RÔLE DE TENANT NE PORTE JAMAIS.
 *
 * Liste RECOPIÉE de `private.refuse_capacites_de_plateforme` (trigger sur
 * `tenant_roles`, posé le 2026-08-28) : c'est la garde SERVEUR, et c'est elle
 * qui fait foi — elle s'applique à toute écriture, d'où qu'elle vienne.
 *
 * ── LE DÉFAUT QU'ELLE FERME (mesuré le 2026-09-16) ─────────────────────────
 * Elle ne contenait que `multi_tenant` et `customer_success`. Or `dg` porte
 * `capabilities_read: "ALL"` — la CHAÎNE, que `capabilities.js` détend en la
 * liste complète des 52 capacités. En mode legacy, le filtre laissait donc
 * passer `marketplace`, `rectorium_licensing` et `rectorium_subscriptions` :
 * le dirigeant d'un établissement client atteignait le back-office commercial
 * de Rectorium. `MasterScopeGate` ne protège pas ce sens-là — il empêche le
 * Master d'ouvrir une vue tenant, jamais l'inverse.
 *
 * Portée exacte, vérifiée : la RLS tenait (`tenant_license_modules` et
 * `billing_contracts` filtrent sur `tenant_id`), donc l'écran s'ouvrait sur
 * ses PROPRES lignes. Frontière d'écran franchie, frontière de données tenue.
 *
 * `ecommerce` n'y figure pas, volontairement : elle est de domaine
 * `plateforme` mais VENDUE, via le module `finance`. Exclure le domaine entier
 * l'aurait fermée à des établissements qui la paient.
 */
const MASTER_ONLY_CAPS = [
  'marketplace', 'customer_success', 'multi_tenant', 'crm', 'sales_ops',
  'corp_finance', 'corp_pmo', 'knowledge_base', 'partner_mgmt', 'corp_ai',
  'corp_academy', 'devops', 'support_ops', 'rectorium_ops', 'rectorium_team',
  'rectorium_licensing', 'rectorium_subscriptions',
];

export const useGovAccess = (roleAssignmentsOverride, { actingAs } = {}) => {
  // `|| {}` : trouvé 2026-09-17 en câblant PortalGate sur ce hook. `useTenant()`
  // est `useContext(TenantContext)` nu — sans Provider au-dessus (test isolé,
  // ou tout composant monté hors de l'arbre applicatif), il rend `null`, et le
  // déstructurer directement fait planter TOUT l'appelant avant même d'avoir pu
  // décider quoi que ce soit. Même défaut que documenté pour un routeur hors
  // Router : un greffon ne doit pas casser son shell hôte. `tenant` reste
  // `undefined`, `mode` retombe sur son défaut `'legacy'` juste en dessous —
  // comportement inchangé partout où le Provider est bien présent.
  const { tenant } = useTenant() || {};
  // `actingAs` (optionnel) : force toute la résolution ci-dessous (legacy ET
  // dynamic) sur un acteur explicite plutôt que sur l'identité effective de
  // useAuth() — nécessaire pour les gestes d'administration qui doivent
  // rester disponibles pour le VRAI compte connecté pendant une
  // impersonification, et jamais hérités par l'identité impersonifiée
  // elle-même (voir useUiLabels.js / Header.jsx, capacité ui_labels).
  const { user: effectiveUser } = useAuth();
  const user = actingAs || effectiveUser;

  const mode          = tenant?.governance_config?.rbac_mode || 'legacy';
  const assignmentsCacheKey = user?.id ? `${user.id}:${tenant?.id || ''}` : null;

  /* ── Mode dynamic : rôles 100% propres à chaque tenant (assemblage de
     capacités à la carte, cf. tenant_roles.permissions + écran Gouvernance
     → Rôles & Permissions) ─────────────────────────────────────────────
     Découverte 2026-07-16 : `checkDynamic` ci-dessous était correct et
     testé (useGovAccess.test.js), mais AUCUN des 150+ appelants réels de
     `useGovAccess()` ne fournissait jamais `roleAssignments` — un tenant
     basculant sur `rbac_mode:'dynamic'` via MigrationWizard.jsx perdait
     donc tous ses accès (sauf master), le pont n'étant jamais alimenté.
     Chargement interne automatique ici, une seule fois par session, SEULEMENT
     si aucun override explicite n'est fourni (compat totale avec les tests
     existants, qui passent toujours un tableau) ET seulement en mode
     'dynamic' (zéro coût réseau pour tout tenant resté en 'legacy', la
     majorité aujourd'hui). */
  const [fetchedAssignments, setFetchedAssignments] = useState(
    () => (assignmentsCacheKey && dynamicAssignmentsCache.get(assignmentsCacheKey)) || []
  );
  // true UNIQUEMENT pendant le fetch en mode dynamic : les consommateurs qui
  // redirigent au premier rendu (HomeDashboard) doivent attendre — sinon la
  // clé de modèle de cockpit (Lot D1) arriverait toujours APRÈS le <Navigate>.
  //
  // Initialisé PARESSEUSEMENT à `mode === 'dynamic'` (et non à `false` en dur) —
  // trouvé le 2026-09-29 : CapabilityGate démonte/remonte cette instance du hook
  // à CHAQUE navigation vers une route protégée (nouveau composant de route =
  // nouveau useGovAccess() = état React frais). Au tout premier rendu de cette
  // instance, l'effet qui met `assignmentsLoading` à `true` puis charge
  // `fetchedAssignments` n'a PAS ENCORE TOURNÉ (les effets s'exécutent après le
  // commit). Avec `false` en dur, ce premier rendu voyait `assignmentsLoading`
  // à `false` ET `fetchedAssignments` à `[]` en même temps : CapabilityGate
  // (qui n'attend QUE sur `mode==='dynamic' && assignmentsLoading`) ne voyait
  // aucune raison d'attendre, évaluait `can()` contre un tableau vide et
  // redirigeait AVANT que la requête réseau qui aurait prouvé le droit ait pu
  // partir — un DG avec `admin:write` confirmé en base se faisait rejeter de
  // `cockpit-recteur` (et de toute autre route à capacité) à chaque clic,
  // boucle qui ramenait systématiquement sur /dashboard/launchpad. `mode` est
  // déjà connu de façon synchrone à cet instant (dérivé de `tenant`, chargé par
  // un Provider AU-DESSUS des routes, jamais remonté par la navigation) : le
  // dériver ici ferme la fenêtre de course sans changer le comportement pour
  // tout appelant qui fournit `roleAssignmentsOverride` (tests) ou reste en
  // mode 'legacy' (aucun coût, comme avant).
  const [assignmentsLoading, setAssignmentsLoading] = useState(
    () => roleAssignmentsOverride === undefined && mode === 'dynamic'
      && !(assignmentsCacheKey && dynamicAssignmentsCache.has(assignmentsCacheKey))
  );
  useEffect(() => {
    if (roleAssignmentsOverride !== undefined) return;
    if (mode !== 'dynamic' || !user?.id) { setFetchedAssignments([]); setAssignmentsLoading(false); return; }
    // Déjà en cache (navigation vers une AUTRE route gardée dans la même
    // session, pas un premier chargement) : pas de nouvel aller-retour
    // réseau, pas de passage par `assignmentsLoading:true` — c'est ce
    // passage qui démontait la vue protégée à chaque clic (voir le
    // commentaire du cache plus haut).
    const cached = assignmentsCacheKey && dynamicAssignmentsCache.get(assignmentsCacheKey);
    if (cached) { setFetchedAssignments(cached); setAssignmentsLoading(false); return; }
    let cancelled = false;
    setAssignmentsLoading(true);
    (async () => {
      // `tenant_roles(*)` (pas une liste de colonnes) : tolérant au décalage
      // de schéma — `cockpit_template_key` (Lot D1 rôles dynamiques) peut ne
      // pas encore exister au moment du déploiement du front ; une colonne
      // explicitement demandée mais absente ferait échouer TOUTE la requête
      // (fail-closed = perte de capacités), `*` non.
      const { data, error } = await supabase
        .from('user_role_assignments')
        .select('role_id, is_active, delegate_start, delegate_end, scope_type, scope_id, role:tenant_roles(*)')
        .eq('user_id', user.id)
        .eq('is_active', true);
      if (!cancelled) {
        // Un échec ici retombe en fail-closed (aucune capacité) SANS AUCUNE
        // trace : un utilisateur peut se voir refuser des droits pourtant
        // correctement accordés en base, sans que rien dans la console
        // n'explique pourquoi (mesuré le 2026-09-29 — IT sans droit d'édition
        // sur Rôles & permissions malgré `governance:write` confirmé en base).
        if (error) console.error('useGovAccess: échec du chargement des affectations dynamiques (fail-closed, aucune capacité accordée) —', error.message || error);
        const resolved = error ? [] : (data || []);
        // Pas de mise en cache sur erreur : la prochaine navigation réessaie
        // plutôt que de figer un fail-closed pour tout le reste de la session.
        if (!error && assignmentsCacheKey) dynamicAssignmentsCache.set(assignmentsCacheKey, resolved);
        setFetchedAssignments(resolved);
        setAssignmentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roleAssignmentsOverride, mode, user?.id, assignmentsCacheKey]);

  const roleAssignments = roleAssignmentsOverride !== undefined ? roleAssignmentsOverride : fetchedAssignments;
  const legacyRole    = user?.role || '';
  const sysRoleId     = resolveSystemRoleId(user);
  const sysRole       = sysRoleId ? SYSTEM_ROLES_MAP[sysRoleId] : null;
  const scopeCampusId  = user?.campus_id   || null;
  const scopeSchoolId  = user?.school_id   || null;
  const scopeCountryId = user?.country_id  || null;
  const scopeRegionId  = user?.region_id   || null;
  const scopeBranchId  = user?.branch_id   || null;
  // Périmètre campus / écoles MULTIPLE (Doyen de plusieurs écoles, enseignant dans
  // plusieurs écoles, intérim) : les affectations actives font foi en mode dynamique.
  const scopeSets = useMemo(() => effectiveScopeSets({
    mode, assignments: roleAssignments, legacy: { campusId: scopeCampusId, schoolId: scopeSchoolId },
  }), [mode, roleAssignments, scopeCampusId, scopeSchoolId]);

  /* ── Backward-compat role helpers ──────────────────────────── */
  const ADMIN_IDS   = ['master', 'dg'];
  const MANAGER_IDS = ['master', 'dg', 'directeur', 'resp_academique', 'gestionnaire', 'registraire'];

  const is        = (...roles)  => roles.some(r => {
    const roleId = resolveSystemRoleId(r);
    return Boolean(roleId) && roleId === sysRoleId;
  });
  const isAdmin   = ()          => ADMIN_IDS.includes(sysRoleId);
  const isManager = ()          => MANAGER_IDS.includes(sysRoleId);
  const isTeacher = ()          => sysRoleId === 'enseignant';
  const isStudent = ()          => sysRoleId === 'etudiant';

  /* ── Dynamic RBAC (mode === 'dynamic') ──────────────────────── */
  const checkDynamic = (capability, action, scopeId = null) => {
    if (!roleAssignments.length) return false;
    return roleAssignments.some(ra => {
      if (!ra.is_active) return false;
      // Les DEUX bornes de délégation (règle du chantier rôles dynamiques,
      // plan §3) : une délégation pas encore commencée n'accorde rien.
      if (ra.delegate_start && new Date(ra.delegate_start) > new Date()) return false;
      if (ra.delegate_end && new Date(ra.delegate_end) < new Date()) return false;
      if (ra.scope_type !== 'tenant' && scopeId && ra.scope_id !== scopeId) return false;
      const perms = ra.role?.permissions?.[capability];
      if (!perms) return false;
      // Forme canonique = objet `{read:true, write:true}` (RolesPermissions.jsx,
      // l'écran d'édition). Trouvé le 2026-10-10 (cockpit Gestionnaire Scolarité
      // vide en prod) : 6 rôles sur 34, créés par des migrations SQL
      // (20261006080000_fusion_registraire_scolarite_admissions.sql et consorts :
      // gouvernance_conseil, vie_etudiante, resp_admissions, sec_general,
      // registraire, dir_campus — identique sur les 4 tenants dynamiques),
      // stockent `permissions[capacité]` en TABLEAU de noms d'action
      // (`["read","write"]`). `perms[action]` sur un tableau est toujours
      // `undefined` → `can()` refusait tout, CapabilityGate redirigeait vers le
      // propre cockpit (refusé) de l'utilisateur en boucle muette. Tolérer les
      // deux formes ici ferme le bug sans réécrire les données en prod.
      return Array.isArray(perms) ? perms.includes(action) : !!perms[action];
    });
  };

  /* ── Modèle de cockpit du rôle affecté (mode dynamic, Lot D1) ──
     Première affectation active (les deux bornes de délégation respectées)
     dont le rôle résout une clé du registre fermé — le cas multirôle avec
     priorité explicite est traité au Lot F. `null` en legacy (zéro coût). */
  const cockpitTemplateKey = useMemo(() => {
    if (mode !== 'dynamic') return null;
    for (const ra of roleAssignments) {
      if (!isAssignmentActive(ra)) continue;
      const key = computeCockpitTemplateKey(ra.role);
      if (key) return key;
    }
    return null;
  }, [mode, roleAssignments]);

  /* ── Garde tenant-IT (réserve F, câblée le 2026-08-21) ──
     « Affectation active » au sens de canUseTenantScopedSurfaces :
     legacy = le profil porte un rôle résolu (l'affectation legacy EST le
     profil) ; dynamic = au moins une affectation respectant is_active ET
     les deux bornes de délégation. Fail-closed : rien de prouvé → false. */
  const hasActiveAssignment = useMemo(() => {
    if (mode !== 'dynamic') return Boolean(sysRoleId);
    return roleAssignments.some((ra) => isAssignmentActive(ra));
  }, [mode, sysRoleId, roleAssignments]);

  /* ── Rôles système endossés par affectation (IT d'école → directeur) ──
     Les capacités de l'IT coopté viennent déjà de son affectation (`can`) ; son MENU et ses ÉCRANS, eux,
     se décidaient sur `profiles.role` seul (constaté 2026-11-02, Jean Paul : capacités posées, menu
     inchangé). Référence stable quand rien n'est endossé (voir le piège de `capOverrides` plus bas). */
  const endorsedSysRoleIds = useMemo(() => {
    if (mode !== 'dynamic') return EMPTY_LIST;
    const ids = rolesSystemeEndosses(roleAssignments);
    return ids.length ? ids : EMPTY_LIST;
  }, [mode, roleAssignments]);

  /* ── Tenant capability override — DISABLED keys block non-masters ──
     BUG RÉEL trouvé le 2026-09-29 (boucle infinie sur AnnuaireView, des
     centaines d'appels/minute à directory_search_general/_restricted) :
     `|| {}` fabrique un objet NEUF à chaque rendu quand aucune surcharge
     n'est configurée (le cas normal — confirmé en base, aucun override pour
     la plupart des tenants). `can` (juste en dessous) dépend de cette
     référence dans son `useMemo` : un objet neuf à chaque rendu fait donc
     RECALCULER `can` à CHAQUE rendu, quel que soit l'état réel des droits.
     Tout appelant qui fait `useCallback(fn, [can])` puis
     `useEffect(effect, [fn])` (le patron de useAnnuaireSnapshot.reload, et
     très probablement d'autres parmi les 150+ appelants de ce hook) se
     retrouve en boucle de re-fetch infinie : chaque reload déclenche un
     re-rendu, qui recrée `can`, qui redéclenche un reload. Le repli sur une
     référence STABLE (module-level, ci-dessus) ferme la boucle : `can` ne
     change d'identité que quand les droits changent réellement. */
  const capOverrides = tenant?.governance_config?.capabilities_override || EMPTY_CAP_OVERRIDES;

  /* ── Unified capability check ───────────────────────────────── */
  const can = useMemo(() => (capability, action = 'read', scopeId = null) => {
    if (!CAPABILITIES_MAP[capability]) return false;
    // Master a accès à tout, sans restriction
    if (sysRoleId === 'master') return true;
    // Domaine opérateur Rectorium → refus pour tout rôle tenant
    if (MASTER_ONLY_DOMAINES.includes(CAPABILITIES_MAP[capability].domaine)) return false;
    if (MASTER_ONLY_CAPS.includes(capability)) return false;
    // GRILLE v2.0.0 : tout tenant (y compris Rectorium Free) accède à 100 %
    // de l'application — la limite du plan Free est le volume d'utilisateurs
    // (50), pas les capacités. L'ancienne garde « free = CORE uniquement »
    // (grille v1.1.0 §3.0, abrogée) est retirée.
    // Capacité désactivée pour ce tenant → refus
    if (capOverrides[capability] === 'DISABLED') return false;
    if (mode === 'dynamic') return checkDynamic(capability, action, scopeId);
    if (!sysRole) return false;
    const grants = sysRole.capabilities || {};
    return (grants[action] || []).includes(capability);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, legacyRole, sysRoleId, roleAssignments, capOverrides]);

  /* ── Scope filter: restreint les requêtes Supabase aux 14 périmètres ── */
  const scopeFilter = useCallback((query, fields = {}) => {
    const {
      campusField  = 'campus_id',
      schoolField  = 'school_id',
      countryField = 'country_id',
      regionField  = 'region_id',
      branchField  = 'branch_id',
    } = fields;
    if (!scopeSets.tenantWide) return applyScopeSets(query, scopeSets, { campusField, schoolField });
    if (scopeCountryId) return query.eq(countryField, scopeCountryId);
    if (scopeRegionId)  return query.eq(regionField,  scopeRegionId);
    if (scopeBranchId)  return query.eq(branchField,  scopeBranchId);
    return query;
  }, [scopeSets, scopeCountryId, scopeRegionId, scopeBranchId]);

  /* ── Portal routing (derives from capabilities — no hardcoded titles) ── */
  const portalAccess = () => resolvePortalType(can);

  return {
    mode,
    userId:         user?.id,
    profileId:      user?.id,
    legacyRole,
    sysRoleId,

    /* Modèle de cockpit du rôle affecté (mode dynamic uniquement, Lot D1
       rôles dynamiques) — à passer à resolveHomeRoute({ rbacMode, cockpitTemplateKey }).
       `assignmentsLoading` : attendre avant toute redirection en dynamic. */
    cockpitTemplateKey,
    assignmentsLoading,

    /* Rôles système endossés par affectation active (mode dynamic) — à passer à
       roleContextOf(…, { endorsedSysRoleIds }) et aux constructeurs de menu. */
    endorsedSysRoleIds,

    /* Garde tenant-IT (réserve F) — à passer à canUseTenantScopedSurfaces
       AVANT d'ouvrir toute surface tenant-scopée it_admin. */
    tenantId: user?.tenant_id || tenant?.id || null,
    hasActiveAssignment,

    /* Compatibility */
    is, isAdmin, isManager, isTeacher, isStudent,

    /* Primary surface — use this everywhere */
    can,

    /* Portal routing — returns a PORTAL_TYPES constant */
    portalAccess,
    PORTAL_TYPES,

    /* Scope helpers — 14 scope types */
    scopeCampusId,
    scopeSchoolId,
    scopeCountryId,
    scopeRegionId,
    scopeBranchId,
    scopeFilter,
    scopeSchoolIds: scopeSets.schoolIds,
    scopeCampusIds: scopeSets.campusIds,
    scopeTenantWide: scopeSets.tenantWide,
    inScope: (entityCampusId) => scopeSets.tenantWide || !scopeSets.campusIds.length || scopeSets.campusIds.includes(entityCampusId),
  };
};
